import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, Alert } from "react-bootstrap";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import axios from "axios";
import { fetchClientData } from "../../redux/client/clientSlice";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import api from "../../utils/api";
import socket from "../../context/socket";

// Helper function to center the aspect crop
function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

// Debounce Effect Hook
function useDebounceEffect(effect, delay, deps) {
  const handler = useRef();

  useEffect(() => {
    handler.current = effect;
  }, [effect]);

  useEffect(() => {
    const handlerFn = () => {
      handler.current();
    };

    const timer = setTimeout(handlerFn, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [...deps, delay]);
}

// Canvas Preview Function
function canvasPreview(image, canvas, crop) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    canvas.width,
    canvas.height
  );
}

// Function to convert canvas to blob
function canvasToBlob(canvas) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, "image/jpeg");
  });
}

// Main component
export default function SignatureCropModal({
  show,
  onHide,
  signatureFile,
  setSignatureFile,
  roomId,
}) {
  const [imgSrc, setImgSrc] = useState("");
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [fileError, setFileError] = useState("");
  const previewCanvasRef = useRef(null);
  const imgRef = useRef(null);
const dispatch = useDispatch();
  useEffect(() => {
    if (signatureFile) {
      const reader = new FileReader();
      reader.onload = () => setImgSrc(reader.result?.toString() || "");
      reader.readAsDataURL(signatureFile);
    }
  }, [signatureFile]);

  function onImageLoad(e) {
    if (imgRef.current) {
      const { width, height } = e.currentTarget;
      setCrop(centerAspectCrop(width, height, 16 / 9));
    }
  }

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop);
      }
    },
    100,
    [completedCrop]
  );

  async function onSave() {
    // Validate that the file exists
    if (!signatureFile) {
      setFileError("A file is required to proceed.");
      return;
    }

    setFileError(""); // Clear error if the file is provided
        Swal.fire({
          text: "Please wait...",
          imageUrl: "https://www.finedge.in/assets/images/download.gif",
          showConfirmButton: false,
          allowOutsideClick: false,
          allowEscapeKey: false,
        });

    if (previewCanvasRef.current) {
      const croppedBlob = await canvasToBlob(previewCanvasRef.current);
      const file = new File([croppedBlob], signatureFile.name, {
        type: croppedBlob.type,
      });

      const formData = new FormData();
      formData.append("document_type", "signature");
      formData.append("_id", roomId);
      formData.append("documentfile", file);
      formData.append("label", "front");
      formData.append("data_status", true);


      try {
        // Send POST request
        const response = await api.post(
         "advisor/cams_upload_kyc_document",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
      
        if (response.status ===200||response.status===201) {
          Swal.fire({
            icon: "success",
            text: response?.message || "Client data updated successfully",
            title: "Success",
            confirmButtonText: "OK",
            customClass: { confirmButton: "swal-confirm-btn" },
          }).then(() => {
            setSignatureFile('')
            onHide();
            dispatch(fetchClientData(roomId));
      
            socket.emit("join", { room: roomId });
            socket.emit("message_to", 
              {
                room: roomId,
                data: { message: response?.message || "Client data updated successfully",
                type: "success",}
              
              })
            console.log("emitted data message_to", roomId, "success");
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Upload Failed',
            text: response.data?.message || 'Something went wrong!',
            confirmButtonColor: '#d33',
          });
        }
      
      } catch (error) {
        console.error("Error uploading files:", error);
        Swal.fire({
          icon: "error",
          title: "Upload Failed",
          text: error?.response?.data?.message || "Something went wrong while uploading.",
          confirmButtonText: "OK",
          customClass: {
            confirmButton: "swal-confirm-btn",
          },
          allowOutsideClick: false,
          allowEscapeKey: false,
        });
      }
    }
  }

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Crop Signature</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {fileError && <Alert variant="danger">{fileError}</Alert>}
        <div className="row">
          <div className="col-md-6">
            {!!imgSrc && (
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                // aspect={16 / 9}
                // minHeight={100}
                aspect={16 / 9}
                minHeight={150}
              >
                <img
                  ref={imgRef}
                  alt="Crop me"
                  src={imgSrc}
                  onLoad={onImageLoad}
                />
              </ReactCrop>
            )}
          </div>
          <div className="col-md-6">
            {!!completedCrop && (
              <canvas
                ref={previewCanvasRef}
                style={{
                  border: "1px solid black",
                  objectFit: "contain",
                  width: completedCrop.width,
                  height: completedCrop.height,
                }}
              />
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button variant="success" onClick={onSave}>
          Save Cropped Image
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
